import React from 'react';
import { Box, Typography, Paper, Button, Chip } from '@mui/material';
import { Star, BusinessCenter } from '@mui/icons-material';

const AdBanner = ({
  type = 'hero',
  isPlaceholder = true,
  adContent = null,
  onContactClick = () => window.open('mailto:justpasteit.ads@gmail.com', '_blank')
}) => {
  // Placeholder content with attractive customer-focused messaging
  const placeholderContent = {
    hero: {
      title: "🚀 Showcase Your Brand to Daily Visitors!",
      subtitle: "Premium advertising space available on JustPaste.it - where developers and content creators discover tools daily",
      features: [
        "High-converting tech audience",
        "Developer-focused community",
        "SEO-optimized visibility",
        "Mobile-responsive placement"
      ],
      // cta: "Advertise Here - Starting at $99/month",
      bgGradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
    },
    sidebar: {
      title: "💼 Promote Your Business",
      subtitle: "Reach engaged developers & creators",
      features: [
        "Premium sidebar placement",
        "Always visible positioning",
        "Targeted tech audience"
      ],
      cta: "Get This Spot - $49/month",
      bgGradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
    },
    content: {
      title: "📈 Boost Your Reach",
      subtitle: "In-content advertising for maximum engagement",
      features: [
        "High engagement rates",
        "Content-relevant audience",
        "Non-intrusive placement"
      ],
      cta: "Secure This Position - $79/month",
      bgGradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
    }
  };

  const content = isPlaceholder ? placeholderContent[type] : adContent;

  const getAdSize = () => {
    switch (type) {
      case 'hero':
        return { minHeight: '200px', maxHeight: '250px' };
      case 'sidebar':
        return { minHeight: '300px', maxHeight: '400px' };
      case 'content':
        return { minHeight: '150px', maxHeight: '200px' };
      default:
        return { minHeight: '200px', maxHeight: '250px' };
    }
  };

  if (!isPlaceholder && adContent) {
    // Render actual customer ad content
    return (
      <Paper
        sx={{
          p: 2,
          mb: 0,
          overflow: 'hidden',
          ...getAdSize()
        }}
      >
        {adContent}
      </Paper>
    );
  }

  // Render attractive placeholder to encourage ad purchases
  return (
    <Paper
      sx={{
        background: content.bgGradient,
        color: 'white',
        p: 3,
        mb: 0,
        position: 'relative',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'transform 0.3s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
        },
        ...getAdSize()
      }}
      onClick={onContactClick}
    >
      {/* Background pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '100px',
          height: '100px',
          background: 'rgba(255,255,255,0.1)',
          borderRadius: '50%',
          transform: 'translate(30px, -30px)'
        }}
      />

      <Box sx={{ position: 'relative', zIndex: 2 }}>
        {/* Header with icon */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <BusinessCenter sx={{ mr: 1 }} />
          <Chip
            label="ADVERTISEMENT SPACE"
            size="small"
            sx={{
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: 'white',
              fontWeight: 'bold'
            }}
          />
        </Box>

        {/* Main content */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 'bold',
            mb: 1,
            fontSize: type === 'sidebar' ? '1.1rem' : '1.3rem'
          }}
        >
          {content.title}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            mb: 2,
            opacity: 0.9,
            fontSize: type === 'sidebar' ? '0.85rem' : '1rem'
          }}
        >
          {content.subtitle}
        </Typography>

        {/* Features list */}
        <Box sx={{ mb: 2 }}>
          {content.features.map((feature, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <Star sx={{ fontSize: '14px', mr: 1, opacity: 0.8 }} />
              <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                {feature}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Stats row */}
        {/* <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Visibility sx={{ fontSize: '16px', mr: 0.5 }} />
            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
              10K+ daily views
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <TrendingUp sx={{ fontSize: '16px', mr: 0.5 }} />
            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
              High CTR audience
            </Typography>
          </Box>
        </Box> */}

        {/* CTA Button */}
        <Button
          variant="contained"
          size={type === 'sidebar' ? 'small' : 'medium'}
          sx={{
            backgroundColor: 'rgba(255,255,255,0.2)',
            color: 'white',
            fontWeight: 'bold',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.3)',
            },
            backdropFilter: 'blur(10px)'
          }}
          fullWidth={type === 'sidebar'}
        >
          {content.cta}
        </Button>
      </Box>
    </Paper>
  );
};

export default AdBanner;
